<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            choose: "Choisir un fichier",
            upload: "Télécharger",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Label du champ
     */
    label?: string
    /**
     * Message d'aide
     */
    hint?: string
    /**
     * Types de fichiers acceptés (ex: "image/png, image/jpeg")
     */
    accept?: string
    /**
     * Taille maximale du fichier en bytes
     */
    maxFileSize?: number
}

type Emits = {
    /**
     * Émis quand un fichier est sélectionné
     */
    upload: [file: File]
}

// ================================
// PROPS & EMITS
// ================================
withDefaults(defineProps<Props>(), {
    label: "",
    hint: "",
    accept: "image/*",
    maxFileSize: 5000000, // 5MB par défaut
})

const emit = defineEmits<Emits>()

// ================================
// STATE
// ================================

/** Référence à l'input file caché */
const fileInput = ref<HTMLInputElement | null>(null)

// ================================
// INTERACTIONS
// ================================
/**
 * Gère la sélection d'un fichier
 */
function onFileSelect(event: Event): void {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0]
    if (file) {
        emit("upload", file)
        // Réinitialise l'input pour permettre de sélectionner le même fichier
        if (fileInput.value) {
            fileInput.value.value = ""
        }
    }
}

/**
 * Ouvre le sélecteur de fichier
 */
function onClickUpload(): void {
    fileInput.value?.click()
}
</script>

<template>
    <div class="w-full flex flex-col gap-2">
        <!-- Label -->
        <label v-if="label" class="text-sm font-medium text-gray-700">
            {{ label }}
        </label>

        <!-- Input file caché -->
        <input
            ref="fileInput"
            type="file"
            :accept="accept"
            class="hidden"
            @change="onFileSelect"
        />

        <!-- Bouton de sélection -->
        <BaseButton
            :label="t('choose')"
            icon="pi pi-upload"
            severity="secondary"
            outlined
            @click="onClickUpload"
        />

        <!-- Message d'aide -->
        <p v-if="hint" class="text-xs text-gray-500">
            {{ hint }}
        </p>
    </div>
</template>
